import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";
import { money, moneyAbs, moneyAdd, moneyDiv, moneyMul, moneyRatioPercent, moneySub, moneyMax } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";

const PERIODS = ["DAILY", "7D", "14D", "30D"];
const SIGNALS = ["ALL", "ACCELERATING", "SLOWDOWN", "STABLE", "MIXED", "NEW_OR_LOW_BASE"];

const PERIOD_META = {
  DAILY: { label: "Harian" },
  "7D": { label: "7 Hari" },
  "14D": { label: "14 Hari" },
  "30D": { label: "30 Hari" },
};

const SIGNAL_META = {
  ACCELERATING: { label: "ACCELERATING" },
  SLOWDOWN: { label: "SLOWDOWN" },
  STABLE: { label: "STABLE" },
  MIXED: { label: "CAMPURAN" },
  NEW_OR_LOW_BASE: { label: "DATA MASIH TIPIS" },
};

const pct = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "-";
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${n.toLocaleString("id-ID", { maximumFractionDigits: 2 })}%`;
};

const compactNumber = (v) => Number(v || 0).toLocaleString("id-ID");

function Icon({ type, size = 22 }) {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  const paths = {
    chart: <><path d="M4 19V5"/><path d="M4 19h16"/><path d="m7 15 3-4 3 2 5-7"/></>,
    box: <><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z"/><path d="m4 7.5 8 4.5 8-4.5"/><path d="M12 12v9"/></>,
    file: <><path d="M6 3h9l4 4v14H6z"/><path d="M14 3v5h5"/><path d="M9 13h6M9 17h6"/></>,
    ads: <><path d="M4 11v2a2 2 0 0 0 2 2h2l2 4h2l-1.7-4.2L20 11V6L8 10H6a2 2 0 0 0-2 1Z"/><path d="M20 6v5"/></>,
    pie: <><path d="M12 3v9h9"/><path d="M20.5 15A9 9 0 1 1 9 3.5"/></>,
    store: <><path d="M4 10h16"/><path d="M5 10v10h14V10"/><path d="M3 10 5 4h14l2 6"/><path d="M9 20v-5h6v5"/></>,
    order: <><path d="M5 4h14v16H5z"/><path d="M8 8h8M8 12h8M8 16h5"/></>,
    refresh: <><path d="M20 11a8 8 0 1 0 1 4"/><path d="M20 5v6h-6"/></>,
    back: <><path d="m15 18-6-6 6-6"/><path d="M9 12h11"/></>,
    trendUp: <><path d="M4 18 10 12l4 4 6-8"/><path d="M15 8h5v5"/></>,
    trendDown: <><path d="M4 6l6 6 4-4 6 8"/><path d="M15 16h5v-5"/></>,
  };
  return <svg {...common}>{paths[type]}</svg>;
}

function MetricCard({ icon, label, value, delta, deltaTone = "up", helper }) {
  const tone = deltaTone === "down" ? "#dc2626" : "#078653";
  return (
    <div style={styles.metricCard}>
      <div style={{ ...styles.metricIcon, ...(styles["metricIcon" + icon.charAt(0).toUpperCase() + icon.slice(1)] || {}) }}><Icon type={icon} size={24} /></div>
      <div style={{ minWidth: 0 }}>
        <div style={styles.metricLabel}>{label}</div>
        <div style={styles.metricValue}>{value}</div>
        {delta !== undefined && (
          <div style={{ ...styles.delta, color: tone }}>
            <span style={{ fontSize: 17, lineHeight: 1 }}>{deltaTone === "down" ? "↓" : "↑"}</span>
            <span>{delta}</span>
            <span style={styles.deltaHelper}>{helper || "dari periode sebelumnya"}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressBar({ value, max, tone = "gray" }) {
  const width = max ? Math.max(5, Math.min(100, (Number(value || 0) / Number(max || 1)) * 100)) : 5;
  const toneStyle = styles["progress" + tone.charAt(0).toUpperCase() + tone.slice(1)] || {};
  return <div style={styles.progressTrack}><div style={{ ...styles.progressFill, ...toneStyle, width: `${width}%` }} /></div>;
}

function SignalBadge({ signal }) {
  const label = SIGNAL_META[signal]?.label || signal || "-";
  const isUp = signal === "ACCELERATING";
  const isDown = signal === "SLOWDOWN";
  return (
    <span style={{ ...styles.signalBadge, ...(isUp ? styles.signalGood : isDown ? styles.signalWarn : {}) }}>
      {(isUp || isDown) && (
        <span style={{ ...styles.signalIcon, ...(isDown ? styles.signalIconDown : {}) }}>
          <Icon type={isUp ? "trendUp" : "trendDown"} size={16} />
        </span>
      )}
      <span>{label}</span>
    </span>
  );
}

export default function AnalyticsDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("DAILY");
  const [dailyStartDate, setDailyStartDate] = useState("");
  const [dailyEndDate, setDailyEndDate] = useState("");
  const [daily, setDaily] = useState(null);
  const [dailyStores, setDailyStores] = useState([]);
  const [dailyOrders, setDailyOrders] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyStoreFilter, setDailyStoreFilter] = useState("ALL");
  const [dailyOrderFilter, setDailyOrderFilter] = useState("REVIEW");
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [signal, setSignal] = useState("ALL");
  const [growthDirection, setGrowthDirection] = useState("ALL");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, accelerating: 0, slowdown: 0, stable: 0, new_or_low_base: 0 });
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadTrend = useCallback(async () => {
    if (!user || user.role !== "OWNER") return;
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ limit: "300" });
      if (q.trim()) params.set("q", q.trim());
      if (signal !== "ALL") params.set("signal", signal);
      const trend = await api.get(`/analytics/sku-trend?${params.toString()}`);
      setRows(trend.data?.items || []);
      setSummary(trend.data?.signal_counts || {});
      setHistory(trend.data?.history || null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Analytics gagal dimuat.");
      setRows([]);
    } finally { setLoading(false); }
  }, [q, signal, user]);

  const loadDaily = useCallback(async (startValue = "", endValue = "") => {
    if (!user || user.role !== "OWNER") return;
    setDailyLoading(true); setError("");
    try {
      const qs = [];
      if (startValue) qs.push(`start_date=${encodeURIComponent(startValue)}`);
      if (endValue) qs.push(`end_date=${encodeURIComponent(endValue)}`);
      const suffix = qs.length ? `?${qs.join("&")}` : "";
      const orderSuffix = suffix ? `${suffix}&limit=300` : "?limit=300";
      // Load the KPI/store summary first. Order detail is only needed when the
      // user is actually reviewing orders, so do not block the initial dashboard render.
      // All three reads are independent. Run them concurrently so the dashboard
      // is gated by the slowest request instead of the sum of summary + store + orders.
      const [res, storeRes, orderData] = await Promise.all([
        api.get(`/analytics/daily-summary${suffix}`),
        api.get(`/analytics/daily-stores${suffix}`),
        api.get(`/analytics/daily-orders${orderSuffix}`).catch(() => ({ data: { items: [] } })),
      ]);
      setDaily(res.data || null);
      setDailyStores(storeRes.data?.items || []);
      setDailyOrders(orderData.data?.items || []);
      setDailyStoreFilter("ALL");
      setDailyOrderFilter("REVIEW");
      setExpandedOrderId(null);
      if (!startValue && !endValue && res.data?.date_from) {
        setDailyStartDate(res.data.date_from);
        setDailyEndDate(res.data.date_to || res.data.date_from);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Data harian gagal dimuat.");
      setDaily(null); setDailyStores([]); setDailyOrders([]);
    } finally { setDailyLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== "OWNER") return;
    if (period === "DAILY") loadDaily(); else loadTrend();
  }, [user, period, loadTrend, loadDaily]);

  const periodConfig = {
    "7D": { qtyKey: "qty_7d", prevKey: "qty_prev_7d", trendKey: "qty_trend_7d_pct", salesKey: "sales_7d", activeKey: "active_days_7d", label: "7D" },
    "14D": { qtyKey: "qty_14d", prevKey: "qty_prev_14d", trendKey: "qty_trend_14d_pct", salesKey: "sales_14d", activeKey: "active_days_14d", label: "14D" },
    "30D": { qtyKey: "qty_30d", prevKey: "qty_prev_30d", trendKey: "qty_trend_30d_pct", salesKey: "sales_30d", activeKey: "active_days_30d", label: "30D" },
  };
  const activeConfig = periodConfig[period] || periodConfig["7D"];

  const filteredRows = useMemo(() => {
    if (growthDirection === "ALL") return rows;
    return rows.filter((r) => {
      const growth = Number(r[activeConfig.trendKey]);
      if (!Number.isFinite(growth)) return false;
      return growthDirection === "UP" ? growth > 0 : growth < 0;
    });
  }, [rows, activeConfig, growthDirection]);

  const periodTotals = useMemo(() => {
    if (period === "DAILY") return null;
    const cur = rows.reduce((s, r) => s + Number(r[activeConfig.qtyKey] || 0), 0);
    const prev = rows.reduce((s, r) => s + Number(r[activeConfig.prevKey] || 0), 0);
    return { qty: cur, prev, growth: prev ? ((cur / prev) - 1) * 100 : null };
  }, [rows, activeConfig, period]);

  const orderStatus = (order) => {
    const gross = money(order.gross_amount);
    const profit = money(order.profit);
    const margin = moneyRatioPercent(profit, gross);
    if (profit < 0) return "BOCOR";
    if (margin < 0.20) return "PERLU_DIPERIKSA";
    return "NORMAL";
  };

  const visibleDailyOrders = useMemo(() => dailyOrders.filter((order) => {
    const storeOk = dailyStoreFilter === "ALL" || order.store_id === dailyStoreFilter;
    const status = orderStatus(order);
    const statusOk = dailyOrderFilter === "ALL" ||
      (dailyOrderFilter === "BOCOR" && status === "BOCOR") ||
      (dailyOrderFilter === "REVIEW" && status === "PERLU_DIPERIKSA");
    return storeOk && statusOk;
  }), [dailyOrders, dailyStoreFilter, dailyOrderFilter]);

  const dailyOrderCounts = useMemo(() => {
    const counts = { ALL: dailyOrders.length, REVIEW: 0, BOCOR: 0 };
    dailyOrders.forEach((o) => { const s = orderStatus(o); if (s === "BOCOR") counts.BOCOR += 1; if (s === "PERLU_DIPERIKSA") counts.REVIEW += 1; });
    return counts;
  }, [dailyOrders]);

  const storeMax = Math.max(...dailyStores.map((s) => Number(s.gross_sales || 0)), 1);
  const fmtDate = (value) => {
    if (!value) return "";
    const p = String(value).split("-");
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : value;
  };

  if (user && user.role !== "OWNER") {
    return <div className="app-shell"><AppTopBar active="analytics" /><main className="content"><div className="alert-error">Akses ditolak.</div></main></div>;
  }

  return (
    <div className="app-shell">
      <AppTopBar active="analytics" />
      <main className="content" style={{ paddingTop: 14, width: "100%", maxWidth: "none", paddingLeft: 24, paddingRight: 24 }}>
      <div className="demand-container" style={styles.container}>
        <header className="demand-header" style={styles.header}>
          <div>
            <div style={styles.titleRow}>
              <h1 style={styles.title}>Demand Intelligence</h1>
              <span style={styles.preview}>PREVIEW</span>
            </div>
            <p style={styles.subtitle}>Analisis profit, biaya, dan pesanan per periode. Pilih rentang tanggal untuk melihat kondisi bisnis terbaru.</p>
          </div>
          <Link to="/dashboard" style={styles.backButton}><Icon type="back" size={21} />Kembali ke Dashboard</Link>
        </header>

        <section className="demand-control" style={styles.controlBar}>
          <div className="demand-period-tabs" style={styles.periodTabs}>
            {PERIODS.map((key) => (
              <button key={key} type="button" onClick={() => setPeriod(key)} style={{ ...styles.periodButton, ...(period === key ? styles.periodActive : {}) }}>
                {PERIOD_META[key].label}
              </button>
            ))}
          </div>
          {period === "DAILY" ? (
            <div className="demand-date-controls" style={styles.dateControls}>
              <label> Dari <input type="date" value={dailyStartDate} onChange={(e) => setDailyStartDate(e.target.value)} /></label>
              <label> Sampai <input type="date" value={dailyEndDate} onChange={(e) => setDailyEndDate(e.target.value)} /></label>
              <button type="button" onClick={() => loadDaily(dailyStartDate, dailyEndDate)} style={styles.refreshButton} disabled={dailyLoading}><Icon type="refresh" size={20} />{dailyLoading ? "Memuat…" : "Refresh"}</button>
            </div>
          ) : (
            <button type="button" onClick={loadTrend} style={styles.refreshButton} disabled={loading}><Icon type="refresh" size={20} />{loading ? "Memuat…" : "Refresh"}</button>
          )}
        </section>

        {error && <div style={styles.error}>{error}</div>}

        {period === "DAILY" ? (
          <>
            <section className="demand-kpi-grid" style={styles.kpiGrid}>
              <MetricCard icon="chart" label="Gross Settlement" value={formatIDR(daily?.gross_sales)} delta={daily?.gross_change_pct != null ? pct(daily.gross_change_pct) : undefined} deltaTone={daily?.gross_change_pct < 0 ? "down" : "up"} />
              <MetricCard icon="box" label="HPP Snapshot" value={formatIDR(daily?.hpp)} delta={daily?.hpp_change_pct != null ? pct(daily.hpp_change_pct) : undefined} deltaTone={daily?.hpp_change_pct < 0 ? "down" : "up"} />
              <MetricCard icon="file" label="Deduction" value={formatIDR(daily?.marketplace_deduction)} delta={daily?.deduction_change_pct != null ? pct(daily.deduction_change_pct) : undefined} deltaTone={daily?.deduction_change_pct > 0 ? "down" : "up"} />
              <MetricCard icon="ads" label="Belanja Iklan (Ads)" value={formatIDR(daily?.ads_spend)} delta={daily?.ads_change_pct != null ? pct(daily.ads_change_pct) : undefined} />
              <MetricCard icon="pie" label="Profit Setelah Ads" value={formatIDR(daily?.net_profit_after_ads)} delta={daily?.profit_change_pct != null ? pct(daily.profit_change_pct) : undefined} deltaTone={daily?.profit_change_pct < 0 ? "down" : "up"} />
            </section>

            <section style={styles.topPanel}>
              <div style={styles.sectionHeader}>
                <div style={styles.sectionTitleWrap}><div style={styles.sectionIcon}><Icon type="pie" size={23} /></div><div><h2 style={styles.sectionTitle}>Biaya yang Mengurangi Profit</h2><div style={styles.sectionHelper}>Total biaya yang terukur pada rentang settlement: deduction marketplace + ads.</div></div></div>
                <span style={styles.orangePill}>{daily?.leakage_rate != null ? `+${Number(daily.leakage_rate).toLocaleString("id-ID", { maximumFractionDigits: 2 })}% dari gross` : ""}</span>
              </div>
              <div className="demand-cost-grid" style={styles.costGrid}>
                <div style={styles.costCard}><div><div style={styles.costLabel}>Marketplace Deduction</div><div style={styles.costValue}>{formatIDR(daily?.marketplace_deduction)}</div></div><span style={styles.grayPill}>{daily?.deduction_rate != null ? `${Number(daily.deduction_rate).toLocaleString("id-ID", { maximumFractionDigits: 2 })}% dari gross` : "-"}</span><ProgressBar value={daily?.marketplace_deduction} max={daily?.gross_sales} tone="green" /></div>
                <div style={styles.costCard}><div><div style={styles.costLabel}>Ads Spend</div><div style={styles.costValue}>{formatIDR(daily?.ads_spend)}</div></div><span style={styles.grayPill}>{daily?.ads_rate != null ? `${Number(daily.ads_rate).toLocaleString("id-ID", { maximumFractionDigits: 2 })}% dari gross` : "-"}</span><ProgressBar value={daily?.ads_spend} max={daily?.gross_sales} tone="blue" /></div>
                <div style={styles.costCard}><div><div style={styles.costLabel}>Total Biaya Pengurang Profit</div><div style={styles.costValue}>{formatIDR(daily?.leakage_total)}</div></div><span style={styles.greenPill}>{daily?.leakage_rate != null ? `+${Number(daily.leakage_rate).toLocaleString("id-ID", { maximumFractionDigits: 2 })}% dari gross` : "-"}</span><ProgressBar value={daily?.leakage_total} max={daily?.gross_sales} tone="orange" /></div>
              </div>
            </section>

            <section style={styles.panel}>
              <div style={styles.sectionHeader}>
                <div style={styles.sectionTitleWrap}><div style={styles.sectionIcon}><Icon type="store" size={23} /></div><div><h2 style={styles.sectionTitle}>Per Toko</h2><div style={styles.sectionHelper}>Ringkasan hasil tiap toko pada rentang settlement yang dipilih.</div></div></div>
                <span style={styles.countPill}>{dailyStores.length} toko</span>
              </div>
              <div style={styles.tableWrap}>
                <table className="demand-intelligence-table" style={styles.table}>
                  <thead><tr>{["TOKO","GROSS","HPP","FEE MARKETPLACE","ADS","PROFIT SEBELUM ADS","PROFIT AFTER ADS","TOTAL FEE + ADS","MARGIN"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {!dailyLoading && dailyStores.length === 0 && <tr><td colSpan={9} style={styles.empty}>Tidak ada data toko pada rentang settlement ini.</td></tr>}
                    {dailyStores.map((store) => {
                      const gross = money(store.gross_sales);
                      const profit = money(store.net_profit_after_ads);
                      const margin = moneyRatioPercent(profit, gross);
                      return <tr key={store.store_id}>
                        <td style={styles.storeCell}><span style={styles.storeMark}><Icon type="store" size={17} /></span><strong>{store.store_name}</strong></td>
                        <td>{formatIDR(gross)}</td><td>{formatIDR(store.hpp)}</td><td>{formatIDR(store.marketplace_deduction)}</td><td>{formatIDR(store.ads_spend)}</td>
                        <td style={styles.boldMoney}>{formatIDR(store.contribution_profit)}</td>
                        <td style={{ ...styles.boldMoney, color: profit < 0 ? "#e11d2a" : "#078653" }}>{formatIDR(profit)}</td>
                        <td>{formatIDR(moneyAdd(store.marketplace_deduction, store.ads_spend))}</td>
                        <td><span style={{ ...styles.marginPill, ...(margin < 0 ? styles.marginBad : margin < 20 ? styles.marginWarn : {}) }}>{margin.toLocaleString("id-ID", { maximumFractionDigits: 1 })}%</span></td>
                      </tr>;
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section style={styles.panel}>
              <div style={styles.sectionHeader}>
                <div style={styles.sectionTitleWrap}><div style={styles.sectionIcon}><Icon type="order" size={23} /></div><div><h2 style={styles.sectionTitle}>Per Pesanan</h2><div style={styles.sectionHelper}>Order dalam rentang settlement. Buka untuk melihat SKU, HPP, margin, dan rincian biaya.</div></div></div>
                <div style={styles.orderControls}>
                  {[["REVIEW","Profit <20%",dailyOrderCounts.REVIEW],["BOCOR","Bocor",dailyOrderCounts.BOCOR],["ALL","Semua",dailyOrderCounts.ALL]].map(([key,label,count]) => <button key={key} type="button" onClick={() => setDailyOrderFilter(key)} style={{ ...styles.filterButton, ...(dailyOrderFilter === key ? styles.filterActive : {}) }}>{label} ({count})</button>)}
                  <select value={dailyStoreFilter} onChange={(e) => setDailyStoreFilter(e.target.value)} style={styles.select}><option value="ALL">Semua toko</option>{dailyStores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}</select>
                </div>
              </div>
              <div style={styles.tableWrap}>
                <table className="demand-intelligence-table" style={styles.table}>
                  <thead><tr>{["TOKO","NO. PESANAN","GROSS","HPP","DEDUCTION","NET","PROFIT","DETAIL"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {visibleDailyOrders.map((order) => {
                      const gross = money(order.gross_amount);
                      const profit = money(order.profit);
                      const orderKey = order.order_id || order.source_order_id || order.id || `${order.store_id || ""}-${gross}-${profit}`;
                      const expanded = expandedOrderId === orderKey;
                      return (
                        <Fragment key={orderKey}>
                          <tr style={{ verticalAlign: "top" }}>
                            <td><span style={styles.orderStore}><span style={styles.storeMark}><Icon type="store" size={15} /></span>{order.store_name}</span></td>
                            <td><span style={styles.orderNo}>{order.order_id || order.source_order_id || "-"}</span></td>
                            <td>{formatIDR(gross)}</td><td>{formatIDR(order.hpp)}</td><td>{formatIDR(order.total_deduction)}</td><td>{formatIDR(order.net_amount)}</td>
                            <td style={{ ...styles.boldMoney, color: profit < 0 ? "#e11d2a" : undefined }}>{formatIDR(profit)}</td>
                            <td><button type="button" onClick={() => setExpandedOrderId(expanded ? null : orderKey)} style={styles.detailButton}>{expanded ? "Tutup" : "Detail"}</button></td>
                          </tr>
                          {expanded && (() => {
                            const skuItems = Array.isArray(order.sku_items) ? order.sku_items : [];
                            const totalQty = skuItems.reduce((sum, item) => sum + Number(item.qty || 0), 0);
                            const fallbackHppUnit = totalQty ? Number(order.hpp || 0) / totalQty : null;
                            const adminFeeRaw = Number(order.admin_fee || 0);
                            const processFeeRaw = Number(order.process_fee || 0);
                            const commissionFeeRaw = Number(order.commission_fee || 0);
                            const totalDeduction = Math.abs(Number(order.total_deduction || 0));
                            // Settlement fee fields may be stored as negative deductions.
                            // Display/audit them as positive costs so the components reconcile to Total Biaya.
                            const adminFee = Math.abs(adminFeeRaw);
                            const processFee = Math.abs(processFeeRaw);
                            const commissionFee = Math.abs(commissionFeeRaw);
                            const mappedFees = adminFee + processFee + commissionFee;
                            const unallocatedFee = Math.max(0, totalDeduction - mappedFees);
                            return (
                              <tr>
                                <td colSpan={8} style={styles.expandedCell}>
                                  <div style={styles.detailPanel}>
                                    <div style={styles.detailSectionLabel}>RINCIAN SKU</div>
                                    <div style={styles.skuTableWrap}>
                                      <table style={styles.skuTable}>
                                        <thead>
                                          <tr>{["SKU","QTY","HARGA / UNIT","HPP REF / UNIT","HPP ORDER / UNIT","LINE SALES","MARGIN SKU"].map((h) => <th key={h}>{h}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                          {skuItems.length ? skuItems.map((item, index) => {
                                            const qty = Number(item.qty || 0);
                                            const lineSales = item.line_sales != null ? money(item.line_sales) : moneyMul(item.unit_price, qty);
                                            const hppOrderUnit = item.hpp_allocated_unit != null ? Number(item.hpp_allocated_unit) : fallbackHppUnit;
                                            const hppRefUnit = item.hpp_reference_unit != null ? Number(item.hpp_reference_unit) : null;
                                            const skuProfit = item.sku_profit_estimate != null
                                              ? Number(item.sku_profit_estimate)
                                              : (hppOrderUnit != null ? lineSales - qty * hppOrderUnit : null);
                                            const skuMargin = item.sku_margin_percent != null
                                              ? Number(item.sku_margin_percent)
                                              : (lineSales ? (skuProfit / lineSales) * 100 : null);
                                            return (
                                              <tr key={item.line_no ?? index}>
                                                <td style={styles.boldMoney}>{item.sku_code || "UNMAPPED"}</td>
                                                <td>{qty}</td>
                                                <td>{formatIDR(item.unit_price)}</td>
                                                <td>{hppRefUnit == null ? "-" : formatIDR(hppRefUnit)}</td>
                                                <td>{hppOrderUnit == null ? "-" : formatIDR(hppOrderUnit)}</td>
                                                <td>{formatIDR(lineSales)}</td>
                                                <td style={styles.boldMoney}>{skuMargin == null ? "-" : pct(skuMargin)}</td>
                                              </tr>
                                            );
                                          }) : (
                                            <tr><td colSpan={7} style={styles.empty}>Rincian SKU belum tersedia untuk order ini.</td></tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>

                                    <div style={styles.auditLabel}>AUDIT PESANAN</div>
                                    <div style={styles.auditTitle}>{order.store_name || "-"} · {order.order_id || order.source_order_id || "-"}</div>
                                    <div style={styles.auditGrid}>
                                      <div style={styles.auditCard}><span style={styles.auditCardLabel}>ADMIN FEE</span><strong style={styles.auditCardValue}>{formatIDR(adminFee)}</strong></div>
                                      <div style={styles.auditCard}><span style={styles.auditCardLabel}>PROCESS FEE</span><strong style={styles.auditCardValue}>{formatIDR(processFee)}</strong></div>
                                      <div style={styles.auditCard}><span style={styles.auditCardLabel}>COMMISSION FEE</span><strong style={styles.auditCardValue}>{formatIDR(commissionFee)}</strong></div>
                                      <div style={{ ...styles.auditCard, borderStyle: "dashed" }}><span style={styles.auditCardLabel}>BELUM TERURAI</span><strong style={styles.auditCardValue}>{formatIDR(unallocatedFee)}</strong></div>
                                      <div style={styles.auditCard}><span style={styles.auditCardLabel}>TOTAL BIAYA</span><strong style={styles.auditCardValue}>{formatIDR(totalDeduction)}</strong></div>
                                    </div>
                                    <div style={styles.auditNote}>
                                      Semua komponen yang terbaca ditampilkan di kartu biaya. <strong>Belum Terurai</strong> adalah selisih antara Total Biaya dan komponen fee yang sudah terpetakan, nilainya tidak diberi label promo/pajak sampai sumbernya tervalidasi.
                                    </div>
                                    <div style={styles.marginRow}>
                                      <span>Order Margin</span><strong>{gross ? pct(profit / gross * 100) : "-"}</strong>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })()}
                        </Fragment>
                      );
                    })}
                    {!dailyLoading && visibleDailyOrders.length === 0 && <tr><td colSpan={8} style={styles.empty}>Tidak ada order pada filter ini.</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : (
          <>
            <section style={styles.panel}>
              <div style={styles.sectionHeader}>
                <div><h2 style={styles.sectionTitle}>Pergerakan SKU — {PERIOD_META[period].label}</h2><div style={styles.sectionHelper}>Membaca momentum demand untuk periode yang dipilih.</div></div>
                <span style={styles.countPill}>Coverage {history?.date_from || "-"} → {history?.date_to || "-"} · {compactNumber(history?.days)} hari</span>
              </div>
              <div className="demand-trend-grid" style={styles.trendControls}>
                <div><label style={styles.inputLabel}>Cari SKU</label><input style={styles.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="contoh: BGS80" /></div>
                <div><label style={styles.inputLabel}>Sinyal</label><select style={styles.input} value={signal} onChange={(e) => setSignal(e.target.value)}>{SIGNALS.map((s) => <option key={s} value={s}>{s === "ALL" ? "Semua" : SIGNAL_META[s]?.label || s}</option>)}</select></div>
              </div>
              <div className="demand-mini-grid" style={styles.kpiMiniGrid}>
                <MetricCard icon="box" label={`QTY ${activeConfig.label}`} value={compactNumber(periodTotals?.qty)} />
                <MetricCard icon="chart" label={`GROWTH ${activeConfig.label}`} value={periodTotals?.growth == null ? "-" : pct(periodTotals.growth)} deltaTone={periodTotals?.growth < 0 ? "down" : "up"} />
                <MetricCard icon="chart" label="ACCELERATING" value={compactNumber(summary.accelerating)} />
                <MetricCard icon="chart" label="SLOWDOWN" value={compactNumber(summary.slowdown)} />
              </div>
            </section>
            <section style={styles.panel}>
              <div style={styles.sectionHeader}>
                <div><h2 style={styles.sectionTitle}>SKU {activeConfig.label}</h2><div style={styles.sectionHelper}>Satu layer demand. Profit SKU belum digunakan.</div></div>
                <div style={styles.skuListControls}>
                  <select aria-label="Filter arah growth" value={growthDirection} onChange={(e) => setGrowthDirection(e.target.value)} style={styles.skuGrowthFilter}>
                    <option value="ALL">Semua arah</option>
                    <option value="UP">Naik ↑</option>
                    <option value="DOWN">Turun ↓</option>
                  </select>
                  <span style={styles.countPill}>{compactNumber(filteredRows.length)} SKU</span>
                </div>
              </div>
              <div style={styles.tableWrap}>
                <table className="demand-intelligence-table" style={styles.table}>
                  <thead><tr><th>SKU</th><th>SINYAL</th><th>QTY</th><th>GROWTH</th><th>SALES</th><th>ACTIVE DAYS</th></tr></thead>
                  <tbody>
                    {loading && <tr><td colSpan={6} style={styles.empty}>Memuat analytics…</td></tr>}
                    {!loading && filteredRows.length === 0 && <tr><td colSpan={6} style={styles.empty}>Tidak ada SKU sesuai filter.</td></tr>}
                    {!loading && filteredRows.map((r) => <tr key={r.product_id}><td style={styles.boldMoney}>{r.sku_code}</td><td><SignalBadge signal={r.demand_signal} /></td><td>{compactNumber(r[activeConfig.qtyKey])}</td><td style={{ fontWeight: 700, color: Number(r[activeConfig.trendKey]) > 0 ? "#078653" : Number(r[activeConfig.trendKey]) < 0 ? "#dc2626" : "#40516b" }}>{pct(r[activeConfig.trendKey])}</td><td>{formatIDR(r[activeConfig.salesKey])}</td><td>{compactNumber(r[activeConfig.activeKey])}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "linear-gradient(135deg,#f5f8fb 0%,#eef3f7 100%)", color: "#12203a", fontFamily: 'Arial,"Helvetica Neue",Helvetica,sans-serif' },
  container: { width: "100%", maxWidth: 1460, margin: "0 auto", padding: "10px 0 42px", boxSizing: "border-box" },
  header: { display: "flex", justifyContent: "space-between", gap: 20, alignItems: "flex-start", marginBottom: 0, padding: "22px 24px 18px", background: "#fff", border: "1px solid #e4e9ef", borderRadius: "16px 16px 0 0", boxSizing: "border-box" },
  titleRow: { display: "flex", alignItems: "center", gap: 12 },
  title: { margin: 0, fontSize: 32, lineHeight: 1.1, letterSpacing: "-.7px" },
  preview: { background: "#eef2f6", color: "#526176", borderRadius: 16, padding: "7px 12px", fontSize: 12, fontWeight: 800 },
  subtitle: { margin: "8px 0 0", color: "#64748b", fontSize: 15 },
  backButton: { display: "flex", alignItems: "center", gap: 8, padding: "11px 17px", border: "1px solid #d6dee8", borderRadius: 9, background: "#fff", color: "#17233d", textDecoration: "none", fontWeight: 600 },
  controlBar: { background: "#fff", border: "1px solid #e4e9ef", borderTop: 0, borderRadius: "0 0 16px 16px", padding: "16px 24px 18px", display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", marginBottom: 22, boxShadow: "0 5px 18px rgba(30,48,75,.04)" },
  periodTabs: { display: "flex", background: "#f0f4f6", borderRadius: 11, padding: 4 },
  periodButton: { border: 0, background: "transparent", color: "#17233d", padding: "12px 27px", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  periodActive: { background: "#5aa398", color: "#fff", boxShadow: "0 3px 8px rgba(90,163,152,.20)" },
  dateControls: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  dateControls: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", color: "#526176", fontSize: 14 },
  dateControlsInput: {},
  dateControlsLabel: {},
  refreshButton: { border: 0, background: "#5aa398", color: "#fff", borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 8, fontWeight: 800, cursor: "pointer", boxShadow: "0 3px 8px rgba(90,163,152,.16)" },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 14, marginBottom: 22 },
  metricCard: { background: "#fff", border: "1px solid #e3e9ef", borderRadius: 14, padding: "19px 18px", minHeight: 142, display: "flex", gap: 14, boxSizing: "border-box", boxShadow: "0 5px 15px rgba(25,45,70,.035)" },
  metricIcon: { width: 48, height: 48, borderRadius: 11, background: "#edf6f3", color: "#16836f", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" },
  metricIconChart: { background: "#e5f4f0", color: "#13816e" },
  metricIconBox: { background: "#e7f0fb", color: "#2877c7" },
  metricIconFile: { background: "#fff0df", color: "#e56f16" },
  metricIconAds: { background: "#eee9ff", color: "#5b38d6" },
  metricIconPie: { background: "#e5f4f0", color: "#13816e" },
  metricLabel: { color: "#40516b", fontSize: 14, marginTop: 2 },
  metricValue: { color: "#111d36", fontSize: 25, fontWeight: 800, letterSpacing: "-.5px", marginTop: 7, whiteSpace: "nowrap" },
  delta: { display: "flex", alignItems: "center", gap: 5, marginTop: 7, fontSize: 12, fontWeight: 700 },
  deltaHelper: { color: "#7a8798", fontWeight: 500, marginLeft: 2 },
  panel: { background: "#fff", border: "1px solid #e2e8ef", borderRadius: 14, padding: 16, marginBottom: 18, boxShadow: "0 5px 15px rgba(25,45,70,.035)" },
  topPanel: { background: "#fff", border: "1px solid #e2e8ef", borderRadius: 15, padding: "20px 22px", marginBottom: 20, boxShadow: "0 5px 15px rgba(25,45,70,.035)" },
  sectionHeader: { display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", marginBottom: 13, flexWrap: "wrap" },
  sectionTitleWrap: { display: "flex", gap: 12, alignItems: "center" },
  sectionIcon: { width: 48, height: 48, borderRadius: 11, background: "#eef3f6", color: "#52657c", display: "flex", alignItems: "center", justifyContent: "center" },
  sectionTitle: { margin: 0, fontSize: 21, letterSpacing: "-.3px" },
  sectionHelper: { color: "#738197", fontSize: 13, marginTop: 4 },
  greenPill: { background: "#e9f7f0", color: "#078653", borderRadius: 18, padding: "8px 13px", fontSize: 12, fontWeight: 700 },
  grayPill: { background: "#eef2f5", color: "#40516b", borderRadius: 18, padding: "8px 12px", fontSize: 12, whiteSpace: "nowrap" },
  orangePill: { background: "#fff0e4", color: "#e56f16", borderRadius: 18, padding: "8px 12px", fontSize: 12, whiteSpace: "nowrap" },
  countPill: { background: "#f2f5f8", color: "#536279", borderRadius: 18, padding: "8px 12px", fontSize: 12, whiteSpace: "nowrap" },
  costGrid: { display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12 },
  costCard: { border: "1px solid #dfe6ed", borderRadius: 12, padding: "16px 15px 13px", minHeight: 112, display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 8 },
  costLabel: { color: "#40516b", fontSize: 14 },
  costValue: { color: "#111d36", fontSize: 22, fontWeight: 800, marginTop: 6 },
  progressTrack: { gridColumn: "1/-1", height: 10, background: "#e7edf2", borderRadius: 8, overflow: "hidden" },
  progressFill: { height: "100%", background: "#6b7c8f", borderRadius: 8 },
  progressGreen: { background: "#58a99b" },
  progressBlue: { background: "#5b9bf0" },
  progressOrange: { background: "#f39a54" },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 1050, fontSize: 13, color: "#40516b", fontFamily: 'Verdana, Geneva, sans-serif' },
  skuListControls: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  skuGrowthFilter: { border: "1px solid #d5dde6", borderRadius: 9, padding: "8px 11px", background: "#fff", color: "#27364f", fontFamily: 'Verdana, Geneva, sans-serif', fontSize: 12, fontWeight: 600, cursor: "pointer" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 1050, fontSize: 13, color: "#40516b" },
  storeCell: { display: "flex", alignItems: "center", gap: 9, color: "#17233d" },
  storeMark: { width: 26, height: 26, borderRadius: 7, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#eef2f5", color: "#65758a" },
  boldMoney: { fontWeight: 800, color: "#15223a" },
  marginPill: { display: "inline-block", background: "#eaf7f0", color: "#087f50", borderRadius: 18, padding: "6px 11px", fontWeight: 700, minWidth: 58, textAlign: "center" },
  marginWarn: { background: "#fff4df", color: "#a45a00" },
  marginBad: { background: "#fde9eb", color: "#d92535" },
  orderControls: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  filterButton: { border: "1px solid #dce3ea", background: "#f8fafb", color: "#40516b", borderRadius: 8, padding: "9px 12px", fontWeight: 700, cursor: "pointer" },
  filterActive: { borderColor: "#e11d2a", color: "#e11d2a", background: "#fff" },
  select: { border: "1px solid #d5dde6", borderRadius: 8, padding: "9px 12px", background: "#fff", color: "#27364f" },
  orderStore: { display: "flex", alignItems: "center", gap: 7, fontWeight: 700, color: "#27364f" },
  orderNo: { background: "#f2f5f8", border: "1px solid #e2e7ec", borderRadius: 7, padding: "7px 10px", color: "#40516b", display: "inline-block" },
  detailButton: { border: "1px solid #d8e0e8", background: "#fff", borderRadius: 8, padding: "8px 15px", cursor: "pointer", color: "#34455e", fontWeight: 600 },
  expandedCell: { padding: "14px 18px !important", background: "#fafbfd" },
  detailGrid: { display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 10 },
  detailPanel: { background: "#fafbfd", border: "1px solid #dfe6ed", borderRadius: 10, padding: "14px 12px 0", boxSizing: "border-box" },
  detailSectionLabel: { color: "#5d6d82", fontSize: 12, fontWeight: 700, marginBottom: 10 },
  skuTableWrap: { width: "100%", overflowX: "auto" },
  skuTable: { width: "100%", borderCollapse: "collapse", minWidth: 900, fontSize: 13, color: "#40516b" },
  auditLabel: { color: "#5d6d82", fontSize: 12, fontWeight: 700, marginTop: 12 },
  auditTitle: { color: "#17233d", fontSize: 14, fontWeight: 800, margin: "5px 0 10px" },
  auditGrid: { display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr)) minmax(0,1.1fr)", gap: 8 },

  auditCard: { border: "1px solid #dfe6ed", borderRadius: 8, padding: "10px 10px 9px", minHeight: 64, background: "#fff", boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "center", gap: 5 },
  auditCardLabel: { display: "block", color: "#5d6d82", fontSize: 10, lineHeight: 1.2, letterSpacing: ".35px", whiteSpace: "nowrap" },
  auditCardValue: { display: "block", color: "#17233d", fontSize: 15, lineHeight: 1.25, fontWeight: 800, whiteSpace: "nowrap" },
  auditNote: { color: "#68778b", fontSize: 12, lineHeight: 1.45, margin: "9px 0 12px" },
  marginRow: { borderTop: "1px solid #dfe6ed", padding: "10px 0 11px", display: "flex", justifyContent: "space-between", color: "#68778b", fontSize: 13 },

  inputLabel: { display: "block", color: "#5d6d82", fontSize: 12, fontWeight: 700, marginBottom: 6 },
  input: { border: "1px solid #d5dde6", borderRadius: 8, padding: "10px 12px", width: "100%", boxSizing: "border-box", background: "#fff", color: "#17233d" },
  trendControls: { display: "grid", gridTemplateColumns: "1fr 220px", gap: 12, marginBottom: 14 },
  kpiMiniGrid: { display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12 },
  signalBadge: { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 16, padding: "6px 10px", background: "#f0f3f6", color: "#596a80", fontSize: 11, fontWeight: 800 },
  signalIcon: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 7, background: "rgba(255,255,255,.7)" },
  signalIconDown: { background: "rgba(255,255,255,.65)" },
  signalGood: { background: "#e8f7ef", color: "#087f50" },
  signalWarn: { background: "#fff2dc", color: "#a45a00" },
  empty: { textAlign: "center", padding: 28, color: "#8491a2" },
  error: { background: "#fff0f1", border: "1px solid #f4bfc4", color: "#b4232f", borderRadius: 10, padding: "11px 14px", marginBottom: 14 },
};

const styleTag = document.createElement("style");
styleTag.textContent = `
  .demand-intelligence-table th,.demand-intelligence-table td{padding:12px 10px;border-bottom:1px solid #edf0f3;text-align:left;white-space:nowrap}
  .demand-intelligence-table thead th{background:#f4f6f8;color:#526176;font-size:11px;font-weight:800;letter-spacing:.25px}
  .skuTable th,.skuTable td{padding:10px 10px;border-bottom:1px solid #e5e9ee;text-align:left;white-space:nowrap}
  .skuTable th{background:#f4f6f8;color:#526176;font-size:11px;font-weight:800;letter-spacing:.2px}
  .skuTable tbody tr:last-child td{border-bottom:0}
  .auditCard span{display:block;color:#5d6d82;font-size:10px;letter-spacing:.35px}
  .auditCard strong{display:block;color:#17233d;font-size:15px;margin-top:4px}
  .demand-intelligence-table tbody tr:hover{background:#fafbfd}
  .demand-intelligence-table tbody tr:last-child td{border-bottom:0}
  .app-shell .demand-container{max-width:1460px}
  .app-shell .content{box-sizing:border-box}
  @media(max-width:900px){.app-shell .content{padding-left:14px!important;padding-right:14px!important}.demand-container{max-width:none!important}.detailGrid{grid-template-columns:1fr 1fr!important}}
  input[type="date"]{border:1px solid #d5dde6;border-radius:8px;background:#fff;padding:10px 12px;color:#27364f}
  @media(max-width:1100px){.demand-kpi-grid{grid-template-columns:repeat(3,minmax(0,1fr))!important}.demand-cost-grid{grid-template-columns:1fr!important}}
  @media(max-width:760px){.demand-container{width:calc(100% - 24px)!important}.demand-header,.demand-control{flex-direction:column!important;align-items:stretch!important}.demand-kpi-grid{grid-template-columns:1fr 1fr!important}.demand-period-tabs{width:100%;overflow:auto}.demand-period-tabs button{flex:1;min-width:90px}.demand-date-controls{justify-content:space-between}.demand-trend-grid{grid-template-columns:1fr!important}.demand-mini-grid{grid-template-columns:1fr 1fr!important}}
`;
if (!document.head.querySelector("[data-demand-intelligence-style]")) { styleTag.setAttribute("data-demand-intelligence-style","true"); document.head.appendChild(styleTag); }

Object.assign(styles, {
  table: { ...styles.table, className: "demand-intelligence-table" },
});